import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { IAppComponentProps } from '../../../../components';
import enUS from 'antd/lib/locale/en_US';
import deDe from 'antd/lib/locale/de_DE';
import {
    Badge,
    Button,
    Card,
    Checkbox,
    ConfigProvider,
    Input,
    message,
    Modal,
    Radio,
    Space,
    Spin,
    Tabs,
    Tooltip,
} from 'antd';
import {
    AppVariableConfiguration,
    deleteSapEndpoint,
    getAllAppVariables,
    getAllSapAdminConfig,
    updateSapAdminConfig,
    upsertAppVariable,
} from '../../services/ReportConfigurationService';
import {
    SapAdminConfig_SapSystemUpdate,
    SapAdminConfig_SystemStatus,
} from '../../graphql/__generated__/SapAdminConfig';
import styles from '../styling/infoportal.module.css';
import { getI18n } from '../../../../utils/Utils';
import { CheckOutlined, CloseOutlined, PlusOutlined, SaveOutlined } from '@ant-design/icons';
import { IntlShape } from 'react-intl/src/types';
import Paragraph from 'antd/es/typography/Paragraph';
import TextArea from 'antd/lib/input/TextArea';
import { v4 as uuidv4 } from 'uuid';
import { AgGridReact } from 'ag-grid-react';
import { CellValueChangedEvent, ColDef } from 'ag-grid-community';
import { getAgGridLocalization } from '../grid/GridUtil';

type ConfigEditParams = {
    currentUser: any;
    distinctEntitlements: string[];
    selectedConfig: SapAdminConfig_SapSystemUpdate;
    intl: IntlShape;
    onDeleteEndpoint?: (id: number) => Promise<void>;
};

type AppVariableEditorProps = {
    intl: IntlShape;
};

type AppVariableRow = AppVariableConfiguration & {
    id: string;
    isDirty: boolean;
    isNew: boolean;
};

const APP_VARIABLE_PREFIX = 'info_portal_';

const APP_VARIABLE_DEFAULT_COL_DEF: ColDef<AppVariableRow> = {
    filter: true,
    minWidth: 180,
    resizable: true,
    sortable: true,
};

const createNewAppVariableRow = (): AppVariableRow => ({
    id: uuidv4(),
    key: APP_VARIABLE_PREFIX,
    value: '',
    isDirty: true,
    isNew: true,
});

const toAppVariableRows = (appVariables: AppVariableConfiguration[]): AppVariableRow[] => (
    appVariables.map((appVariable) => ({
        ...appVariable,
        id: appVariable.key,
        isDirty: false,
        isNew: false,
    }))
);

const hasDuplicateAppVariableKeys = (appVariables: AppVariableRow[]): boolean => {
    const keys = appVariables.map((appVariable) => appVariable.key.trim());
    return new Set(keys).size !== keys.length;
};

const createAppVariableColumnDefs = (intl: IntlShape): ColDef<AppVariableRow>[] => [
    {
        editable: (params) => Boolean(params.data?.isNew),
        field: 'key',
        flex: 1,
        headerName: intl.formatMessage({
            id: 'reporting-configuration.app-variables.key',
            defaultMessage: 'Property key',
        }),
        minWidth: 260,
    },
    {
        editable: true,
        field: 'value',
        flex: 2,
        headerName: intl.formatMessage({
            id: 'reporting-configuration.app-variables.value',
            defaultMessage: 'Value',
        }),
        minWidth: 300,
    },
];

const validateUrl = (url:string)=> {
    const regex = new RegExp(`^http(s)?(://)(.+)(:[\\d]+)?[^/]$`, 'i');
    return regex.test(url);
};

const validatePath = (path:string)=> {
  const regex = RegExp('^([/])([a-z0-9A-Z])(.)+');
  return regex.test(path);
};

const DisplayAdd = ({ selectedConfig, intl }: ConfigEditParams) => {
    const [system, setSystem] = useState<string>(null);
    const [url, setUrl] = useState<string>(null);
    const [path, setPath] = useState<string>(null);
    const [userName, setUserName] = useState<string>(null);
    const [description, setDescription] = useState<string>(null);
    const [filter, setFilter] = useState<string>(null);
    const [addEndpointKey, setAddEndpointKey] = useState<string>(uuidv4());
    const [hasCopiedConfig, setHasCopiedConfig] = useState<boolean>(false);

    useEffect(() => {
        setHasCopiedConfig(!!sessionStorage.getItem('copiedConfig'));
    }, []);

    const pasteConfig = () => {
        try {
            const copiedConfig = JSON.parse(sessionStorage.getItem('copiedConfig'));
            if (!copiedConfig) throw new Error('No config found');
            setUrl(copiedConfig.url || null);
            setPath(copiedConfig.path || null);
            setUserName(copiedConfig.userName || null);
            setDescription(copiedConfig.description || null);
            setSystem(copiedConfig.systemNameFreeText || null);
            setFilter(copiedConfig.filerResultsRule || null);
            message.success(intl.formatMessage({ id: 'config.paste.success', defaultMessage: 'Config pasted!' }));
        } catch (err) {
            message.error(intl.formatMessage({ id: 'config.paste.error', defaultMessage: 'Failed to paste config.' }));
        }
    };

    const addToConfig = (_e: any) => {
        let failedValidation = false;
        if (!validateUrl(url)) {
            message.error('The url is invalid.');
            failedValidation = true;
        }
        if (!validatePath(path)) {
            message.error('The path is invalid.');
            failedValidation = true;
        }
        if (!system) {
            message.error('The system is invalid');
            failedValidation = true;
        }
        if (!userName) {
            message.error('The userName is invalid.');
            failedValidation = true;
        }
        if (!description) {
            message.error('The description is invalid.');
            failedValidation = true;
        }

        if (!failedValidation) {
            const newSapSystem: SapAdminConfig_SystemStatus = {
                url,
                path,
                userName,
                secretPlaceholder: '',
                vaultProperty:'TO_BE_FILLED_BY_BACKEND',
                systemName: 'MDB',
                systemNameFreeText: system,
                active: false,
                filerResultsRule: filter,
                id: null,
                placeHolderConfigured: false,
                vaultPropertyFound: false,
                reachable: false,
                description,
            };

            selectedConfig.systemStatusList.push(newSapSystem);

            message.info('Remember to save to persist new sap config or changes will be lost');
            setUrl(null);
            setPath(null);
            setUserName(null);
            setDescription(null);
            setSystem(null);
            setFilter(null);
            setAddEndpointKey(uuidv4());
        }
    };

    const setFilterRule = (criteriaText: string) => {
        if (!criteriaText) {
            setFilter(null);
            return;
        }

        setFilter(`[{"additionalCriteria": "${criteriaText}"}]`);
    };

    return (
        <div key={addEndpointKey}>
            <div className={styles.reportConfigGrid}>
                <Paragraph>URL</Paragraph>
                <Tooltip
                    title={
                        "Please make sure that the url starts with http or https and doesn't end with a forward slash"
                    }
                >
                    <Input
                        type="text"
                        placeholder="https://example.com"
                        allowClear={true}
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                    />
                </Tooltip>
            </div>
            <div className={styles.reportConfigGrid}>
                <Paragraph>Path</Paragraph>
                <Tooltip title={'Please make sure that the path starts with a forward slash'}>
                    <Input
                        type="text"
                        placeholder="/sap/systems/path"
                        allowClear={true}
                        value={path}
                        onChange={(e) => setPath(e.target.value)}
                    />
                </Tooltip>
            </div>
            <div className={styles.reportConfigGrid}>
                <Paragraph>Username</Paragraph>
                <Tooltip title={'Username'}>
                    <Input
                        type="text"
                        placeholder="username"
                        allowClear={true}
                        value={userName}
                        onChange={(e) => setUserName(e.target.value)}
                    />
                </Tooltip>
            </div>
            <div className={styles.reportConfigGrid}>
                <Paragraph>System Code</Paragraph>
                <Input
                    type="text"
                    placeholder="Endpoint system name"
                    value={system}
                    allowClear={true}
                    onChange={(e) => setSystem(e.target.value)}
                />
            </div>
            <div className={styles.reportConfigGrid}>
                <Paragraph>Description</Paragraph>
                <Tooltip title={'Enter the description of the endpoint'}>
                    <TextArea
                        rows={2}
                        placeholder="Description of the new endpoint"
                        allowClear={true}
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </Tooltip>
            </div>

            <div className={styles.reportConfigGrid}>
                <Paragraph>Filter</Paragraph>
                <Tooltip title={'Enter the filter of the endpoint'}>
                    <TextArea
                        rows={4}
                        placeholder="The rules to filter the row"
                        allowClear={true}
                        value={filter}
                        onChange={(e) => setFilterRule(e.target.value)}
                    />
                </Tooltip>
            </div>

            {hasCopiedConfig && (
                <div>
                    <Button type={'default'} style={{ marginBottom: '12px',width: '120px' }} onClick={pasteConfig}>
                        {intl.formatMessage({ id: 'paste.config', defaultMessage: 'Paste Config' })}
                    </Button>
                </div>
            )}

            <Button type={'primary'} style={{ width: '120px' }} onClick={(e) => addToConfig(e)}>
                {intl.formatMessage({ id: 'add' })}
            </Button>
        </div>
    );
};

const DisplayEdit = ({ currentUser, distinctEntitlements, selectedConfig, intl, onDeleteEndpoint }: ConfigEditParams) => {
    const [selectedEndpoint, setSelectedEndpoint] = useState<SapAdminConfig_SystemStatus>(null);
    const [_flag, setFlag] = useState<boolean>(false);

    const fetchCurrentFilterConfiguration = (): LogSystemCompanyCodes[] => {
        if (selectedEndpoint?.filerResultsRule) {
            try {
                return JSON.parse(selectedEndpoint.filerResultsRule);
            } catch (error) {
                console.error(
                    `Something is wrong with the existing config ${selectedEndpoint?.filerResultsRule}`,
                    error
                );
            }
        }

        return [];
    };

    const fetchFilterRule = () => {
        const logSystemCompanyCodes = fetchCurrentFilterConfiguration();
        if (logSystemCompanyCodes.length) {
            return logSystemCompanyCodes[0].additionalCriteria;
        }
        return null;
    };

    const hasOldConfig = ()=> {
        const logSystemCompanyCodes = fetchCurrentFilterConfiguration();
        return logSystemCompanyCodes.length && logSystemCompanyCodes[0].logSystem;
    }

    const setFilterRule = (criteriaText: string) => {
        if (!criteriaText) {
            selectedEndpoint.filerResultsRule = null;
            return;
        }

        selectedEndpoint.filerResultsRule = `[{"additionalCriteria": "${criteriaText}"}]`;
    };

    const onEndpointSelectionChange = (event) => {
        selectedConfig.systemStatusList.forEach((urlEndpoint) => {
            if (`${urlEndpoint.url}/${urlEndpoint.path}` === event.target.value) {
                setSelectedEndpoint(urlEndpoint);
            }
        });
    };

    type LogSystemCompanyCodes = {
        additionalCriteria: string;
        logSystem: string;
        companyCodes: string[];
    };

    useEffect(()=> {
        console.log(selectedEndpoint);
    })

    return (
        <div>
            <Radio.Group onChange={onEndpointSelectionChange}>
                {selectedConfig.systemStatusList.map((endpoint) => (
                    <Radio key={`filter-${endpoint.url}/${endpoint.path}`} value={`${endpoint.url}/${endpoint.path}`}>
                        {`${endpoint.url}${endpoint.path} (${endpoint.systemNameFreeText})`}
                    </Radio>
                ))}
            </Radio.Group>

            <div hidden={!selectedEndpoint} style={{ borderTop: '1px solid #dddddd', marginTop: '10px' }}>
                {selectedEndpoint && (
                    <div>
                        <div>
                            <h4>Current Properties</h4>

                            <Paragraph>Please be careful when modifying existing properties.</Paragraph>

                            <div className={styles.reportConfigGrid}>
                                <Paragraph>URL</Paragraph>
                                <Tooltip
                                    title={
                                        "Please make sure that the url starts with http or https and doesn't end with a forward slash"
                                    }
                                >
                                    <Input
                                        key={`${selectedEndpoint?.id}-edit-url`}
                                        type="text"
                                        placeholder="https://example.com"
                                        allowClear={true}
                                        defaultValue={selectedEndpoint.url}
                                        onChange={(e) => (selectedEndpoint.url = e.target.value)}
                                    />
                                </Tooltip>
                            </div>
                            <div className={styles.reportConfigGrid}>
                                <Paragraph>Path</Paragraph>
                                <Tooltip title={'Please make sure that the path starts with a forward slash'}>
                                    <Input
                                        key={`${selectedEndpoint?.id}-edit-path`}
                                        type="text"
                                        placeholder="/sap/systems/path"
                                        allowClear={true}
                                        defaultValue={selectedEndpoint.path}
                                        onChange={(e) => (selectedEndpoint.path = e.target.value)}
                                    />
                                </Tooltip>
                            </div>
                            <div className={styles.reportConfigGrid}>
                                <Paragraph>User</Paragraph>
                                <Tooltip title={'Username'}>
                                    <Input
                                        key={`${selectedEndpoint?.id}-edit-username`}
                                        type="text"
                                        placeholder="username"
                                        allowClear={true}
                                        defaultValue={selectedEndpoint.userName}
                                        onChange={(e) => (selectedEndpoint.userName = e.target.value)}
                                    />
                                </Tooltip>
                            </div>
                            <div className={styles.reportConfigGrid}>
                                <Paragraph>Vault Property name</Paragraph>
                                <Tooltip title={'Vault Property name'}>
                                    <Input
                                        key={`${selectedEndpoint?.id}-edit-vault-property`}
                                        type="text"
                                        disabled={true}
                                        defaultValue={selectedEndpoint.vaultProperty}
                                    />
                                </Tooltip>
                            </div>
                            <div className={styles.reportConfigGrid}>
                                <Paragraph>System Name</Paragraph>

                                <Input
                                    key={`${selectedEndpoint?.id}-edit-system-name`}
                                    type="text"
                                    placeholder="system name"
                                    allowClear={true}
                                    defaultValue={selectedEndpoint.systemNameFreeText}
                                    onChange={(e) => (selectedEndpoint.systemNameFreeText = e.target.value)}
                                />
                            </div>

                            <div className={styles.reportConfigGrid}>
                                <Paragraph>Description</Paragraph>
                                <Tooltip title={'Enter the description of the endpoint'}>
                                    <TextArea
                                        key={`${selectedEndpoint?.id}-edit-text-description`}
                                        rows={2}
                                        defaultValue={selectedEndpoint.description}
                                        placeholder="Description of the new endpoint"
                                        allowClear={true}
                                        onChange={(e) => (selectedEndpoint.description = e.target.value)}
                                    />
                                </Tooltip>
                            </div>

                            <div className={styles.reportConfigGrid}>
                                <Paragraph>Filter</Paragraph>
                                <Tooltip title={'Enter the filter of the endpoint'}>
                                    <TextArea
                                        key={`${selectedEndpoint?.id}-edit-text-filter`}
                                        rows={4}
                                        defaultValue={fetchFilterRule()}
                                        placeholder="The rules to filter the row"
                                        allowClear={true}
                                        onChange={(e) => setFilterRule(e.target.value)}
                                    />
                                </Tooltip>
                            </div>
                        </div>
                        <div
                            hidden={!hasOldConfig()}
                            key={`${selectedEndpoint?.id}-endpoint-filters`}
                        >
                            <Paragraph>Current row filters:</Paragraph>

                            {fetchCurrentFilterConfiguration().filter(item=>item.companyCodes).map((filterRule) => (
                                <div key={`badge-${filterRule.logSystem} (${filterRule.companyCodes.join(',')})`}>
                                    <Badge
                                        color={'geekblue'}
                                        text={`${filterRule.logSystem} (${filterRule.companyCodes.join(',')})`}
                                    />
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
            <Button hidden={!selectedEndpoint} type={'default'} style={{ marginTop: '16px', width: '120px' }} onClick={() => {
                if (selectedEndpoint) {
                    sessionStorage.setItem('copiedConfig', JSON.stringify(selectedEndpoint));
                    message.success(intl.formatMessage({ id: 'config.copy.success', defaultMessage: 'Config copied!' }));
                } else {
                    message.error(intl.formatMessage({ id: 'config.copy.error', defaultMessage: 'No config selected.' }));
                }
            }}>
                {intl.formatMessage({ id: 'copy.config', defaultMessage: 'Copy Config' })}
            </Button>
            <Button
                hidden={!selectedEndpoint}
                danger={true}
                style={{ marginTop: '16px', marginLeft: '8px', width: '120px' }}
                onClick={() => {
                    if (!selectedEndpoint?.id) {
                        message.error(intl.formatMessage({ id: 'config.delete.no-id', defaultMessage: 'Cannot delete an endpoint without a saved ID.' }));
                        return;
                    }
                    Modal.confirm({
                        title: intl.formatMessage({ id: 'config.delete.confirm.title', defaultMessage: 'Delete Endpoint' }),
                        content: intl.formatMessage(
                            { id: 'config.delete.confirm.content', defaultMessage: 'Are you sure you want to delete endpoint {url}{path}?' },
                            { url: selectedEndpoint.url, path: selectedEndpoint.path }
                        ),
                        okText: intl.formatMessage({ id: 'config.delete.confirm.ok', defaultMessage: 'Delete' }),
                        okButtonProps: { danger: true },
                        cancelText: intl.formatMessage({ id: 'cancel', defaultMessage: 'Cancel' }),
                        onOk: () => onDeleteEndpoint?.(selectedEndpoint.id),
                    });
                }}
            >
                {intl.formatMessage({ id: 'config.delete.button', defaultMessage: 'Delete' })}
            </Button>
        </div>
    );
};

const AppVariableEditor = ({ intl }: AppVariableEditorProps) => {
    const [appVariableRows, setAppVariableRows] = useState<AppVariableRow[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [saving, setSaving] = useState<boolean>(false);
    const appVariableColumnDefs = useMemo(() => createAppVariableColumnDefs(intl), [intl]);

    useEffect(() => {
        getAllAppVariables()
            .then((variables) => {
                setAppVariableRows(toAppVariableRows(variables));
            })
            .catch((error) => {
                console.error(error);
                message.error(
                    intl.formatMessage({
                        id: 'reporting-configuration.app-variables.load-error',
                        defaultMessage: 'Unable to load application properties.',
                    })
                );
            })
            .finally(() => {
                setLoading(false);
            });
    }, [intl]);

    const onAppVariableCellValueChanged = (event: CellValueChangedEvent<AppVariableRow>) => {
        if (!event.data || event.newValue === event.oldValue) {
            return;
        }

        const changedField = event.column.getColId();
        const changedValue = String(event.newValue ?? '');

        setAppVariableRows((appVariables) => appVariables.map((appVariable) => {
            if (appVariable.id !== event.data.id) {
                return appVariable;
            }

            return {
                ...appVariable,
                key: changedField === 'key' ? changedValue : appVariable.key,
                value: changedField === 'value' ? changedValue : appVariable.value,
                isDirty: true,
            };
        }));
    };

    const saveAppVariables = async (): Promise<void> => {
        const changedAppVariables = appVariableRows.filter((appVariable) => appVariable.isDirty);

        if (changedAppVariables.some((appVariable) => appVariable.key.trim().length === APP_VARIABLE_PREFIX.length)) {
            message.error(
                intl.formatMessage({
                    id: 'reporting-configuration.app-variables.key-required',
                    defaultMessage: 'Enter a property key.',
                })
            );
            return;
        }

        if (changedAppVariables.some((appVariable) => !appVariable.key.trim().startsWith(APP_VARIABLE_PREFIX))) {
            message.error(
                intl.formatMessage({
                    id: 'reporting-configuration.app-variables.key-prefix',
                    defaultMessage: 'Property keys must start with info_portal_.',
                })
            );
            return;
        }

        if (hasDuplicateAppVariableKeys(appVariableRows)) {
            message.error(
                intl.formatMessage({
                    id: 'reporting-configuration.app-variables.duplicate-key',
                    defaultMessage: 'Each property key must be unique.',
                })
            );
            return;
        }

        setSaving(true);

        try {
            const saveResults = await Promise.all(changedAppVariables.map((appVariable) => (
                upsertAppVariable({
                    key: appVariable.key.trim(),
                    value: appVariable.value,
                })
            )));

            if (saveResults.some((saved) => !saved)) {
                throw new Error('One or more application properties could not be saved.');
            }

            const updatedAppVariables = await getAllAppVariables();
            setAppVariableRows(toAppVariableRows(updatedAppVariables));

            message.success(
                intl.formatMessage({
                    id: 'reporting-configuration.app-variables.save-success',
                    defaultMessage: 'Application property saved.',
                })
            );
        } catch (error) {
            console.error(error);
            message.error(
                intl.formatMessage({
                    id: 'reporting-configuration.app-variables.save-error',
                    defaultMessage: 'Unable to save the application property.',
                })
            );
        } finally {
            setSaving(false);
        }
    };

    const addAppVariableRow = () => {
        setAppVariableRows((appVariables) => [createNewAppVariableRow(), ...appVariables]);
    };

    return (
        <Spin spinning={loading || saving}>
            <div>
                <h4>
                    {intl.formatMessage({
                        id: 'reporting-configuration.app-variables.title',
                        defaultMessage: 'Application properties',
                    })}
                </h4>

                <div className={styles.appVariableToolbar}>
                    <Button disabled={saving} icon={<PlusOutlined />} onClick={addAppVariableRow}>
                        {intl.formatMessage({
                            id: 'reporting-configuration.app-variables.add',
                            defaultMessage: 'Add property',
                        })}
                    </Button>
                    <Button
                        disabled={saving || !appVariableRows.some((appVariable) => appVariable.isDirty)}
                        icon={<SaveOutlined />}
                        loading={saving}
                        type="primary"
                        onClick={() => void saveAppVariables()}
                    >
                        {intl.formatMessage({
                            id: 'reporting-configuration.app-variables.save-changes',
                            defaultMessage: 'Save changes',
                        })}
                    </Button>
                </div>

                <div className={`ag-theme-balham ${styles.appVariableGrid}`}>
                    <AgGridReact<AppVariableRow>
                        columnDefs={appVariableColumnDefs}
                        defaultColDef={APP_VARIABLE_DEFAULT_COL_DEF}
                        getRowId={(params) => params.data.id}
                        localeText={getAgGridLocalization(intl)}
                        onCellValueChanged={onAppVariableCellValueChanged}
                        pagination={true}
                        paginationAutoPageSize={true}
                        rowData={appVariableRows}
                        singleClickEdit={true}
                        stopEditingWhenCellsLoseFocus={true}
                    />
                </div>
            </div>
        </Spin>
    );
};

const ReportingConfiguration: FunctionComponent<IAppComponentProps> = (props) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [selectedConfig, setSelectedConfig] = useState<SapAdminConfig_SapSystemUpdate>(null);
    const [sapConfigurationList, setSapConfigurationList] = useState<SapAdminConfig_SapSystemUpdate[]>();
    const [adminActiveTab, setAdminActiveTab] = useState<string>('configure-endpoints');
    const [endpointActiveTab, setEndpointActiveTab] = useState<string>('enable-disable');

    useEffect(() => {
        setLoading(true);

        getAllSapAdminConfig()
            .then((data: SapAdminConfig_SapSystemUpdate[]) => {
                data.forEach((item) => {
                    if (item.reportName === 'sr3') {
                        setSelectedConfig(item);
                    }
                });
                setSapConfigurationList(data);
            })
            .finally(() => {
                setLoading(false);
            });
    }, []);

    const onSelectionChecked = (val, system: SapAdminConfig_SystemStatus) => {
        selectedConfig.systemStatusList.forEach((item) => {
            if (item.url === system.url && item.path === system.path) {
                item.active = val.target.checked;
            }
        });
    };

    const selectReport = (event) => {
        sapConfigurationList.forEach((item) => {
            if (item.reportName === event.target.value) {
                setSelectedConfig(item);
            }
        });
    };

    const handleDeleteEndpoint = (id: number): Promise<void> => {
        setLoading(true);
        return deleteSapEndpoint(id)
            .then(() => getAllSapAdminConfig())
            .then((data: SapAdminConfig_SapSystemUpdate[]) => {
                let updatedSelected: SapAdminConfig_SapSystemUpdate = null;
                data.forEach((item) => {
                    if (item.reportName === selectedConfig.reportName) {
                        updatedSelected = item;
                    }
                });
                setSelectedConfig(updatedSelected);
                setSapConfigurationList(data);
                message.success(props.intl.formatMessage({ id: 'config.delete.success', defaultMessage: 'Endpoint deleted successfully.' }));
            })
            .catch((error) => {
                console.error(error);
                Modal.warning({
                    title: props.intl.formatMessage({ id: 'config.delete.error', defaultMessage: 'Error occurred while deleting endpoint.' }),
                });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const submitData = () => {

        let failedValidation = false;

        for (let i = 0; i < selectedConfig.systemStatusList.length; i++) {
            const ithConfig = selectedConfig.systemStatusList[i];

            if (!validateUrl(ithConfig.url)) {
                message.error(`The url at index ${i} is not valid`);
                failedValidation = true;
            }
            if (!validatePath(ithConfig.path)) {
                message.error(`The path at index ${i} is not valid`);
                failedValidation = true;
            }
            if (!ithConfig.systemNameFreeText) {
                message.error(`Please specify the system name for ${i}`);
                failedValidation = true;
            }
            if (!ithConfig.userName) {
                message.error(`The username for index ${i} is invalid`);
                failedValidation = true;
            }

            if (failedValidation) {
                return;
            }
        }

        setLoading(true);
        updateSapAdminConfig(selectedConfig)
            .then((_) => getAllSapAdminConfig())
            .then((data: SapAdminConfig_SapSystemUpdate[]) => {
                data.forEach((item) => {
                    if (item.reportName === selectedConfig.reportName) {
                        setSelectedConfig(item);
                    }
                });
                setSapConfigurationList(data);
                Modal.success({
                    title: getI18n('Download Success', 'Success', props.intl),
                    content: getI18n('Download Success', 'Success', props.intl),
                    okText: 'OK',
                });
            })
            .catch((error) => {
                console.error(error);
                Modal.warning({
                    title: getI18n('error-occurred-while-performing-save', 'Error occurred while saving', props.intl),
                });
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <ConfigProvider locale={localStorage.getItem('locale') === 'en' ? enUS : deDe}>
            <Spin size={'large'} spinning={loading}>
                <Card size={'small'} style={{ backgroundColor: '#f1f3f5' }}>
                    <h3>{props.intl.formatMessage({ id: 'report-admin-menu-label' })}</h3>
                    <Tabs activeKey={adminActiveTab} onChange={setAdminActiveTab}>
                        <Tabs.TabPane
                            tab={props.intl.formatMessage({
                                id: 'reporting-configuration.endpoints.tab',
                                defaultMessage: 'Configure Endpoints',
                            })}
                            key="configure-endpoints"
                        >
                            <div className={styles.selfBilledSimpleSearchContainer} style={{ marginLeft: '10px' }}>
                                <div hidden={!sapConfigurationList?.length}>
                                    <h4>{props.intl.formatMessage({ id: 'cor-report-select' })}</h4>
                                    <Radio.Group onChange={selectReport} defaultValue={'sr3'}>
                                        <Space direction={'vertical'}>
                                            {sapConfigurationList?.map((config) => (
                                                <Radio
                                                    data-testid={`${config.reportName}-config`}
                                                    className="info-radio"
                                                    key={`${config.reportName}`}
                                                    value={config.reportName}
                                                >
                                                    {`${config.reportName}-${config.description}`}
                                                </Radio>
                                            ))}
                                        </Space>
                                    </Radio.Group>
                                </div>

                                <div hidden={!selectedConfig}>
                                    <Tabs activeKey={endpointActiveTab} onChange={setEndpointActiveTab}>
                                <Tabs.TabPane
                                    tab={props.intl.formatMessage({ id: 'enable-disable' })}
                                    key={'enable-disable'}
                                >
                                    {selectedConfig?.systemStatusList.map((system, idx) => (
                                        <div key={`url-${idx}-for-${selectedConfig.reportName}`}>
                                            <Tooltip title={`(${system.userName}, ${system.secretPlaceholder}, ${system.vaultProperty})`}>
                                                <Checkbox
                                                    key={`checkbox-${idx}-for-${selectedConfig.reportName}`}
                                                    defaultChecked={system.active}
                                                    onChange={(e) => onSelectionChecked(e, system)}
                                                >
                                                    {`${system.url}${system.path} (${system.systemNameFreeText})`}
                                                </Checkbox>

                                                <div style={{ marginLeft: '5px', display: 'inline-block' }}>
                                                    <Tooltip
                                                        title={`Could reach host: ${system.reachable}. Property ${system.vaultProperty} was found ${system.vaultPropertyFound}`}
                                                    >
                                                        {system.reachable && system.vaultPropertyFound ? (
                                                            <CheckOutlined style={{ cursor: 'pointer' }} />
                                                        ) : (
                                                            <CloseOutlined style={{ cursor: 'pointer' }} />
                                                        )}
                                                    </Tooltip>
                                                </div>
                                            </Tooltip>
                                        </div>
                                    ))}
                                </Tabs.TabPane>
                                <Tabs.TabPane
                                    tab={props.intl.formatMessage({ id: 'draft-document-row-options-edit' })}
                                    key="edit-config"
                                >
                                    <DisplayEdit
                                        key={`${selectedConfig?.reportName}-edit-config`}
                                        currentUser={props.currentUser}
                                        selectedConfig={selectedConfig}
                                        intl={props.intl}
                                        distinctEntitlements={props.distinctEntitlements}
                                        onDeleteEndpoint={handleDeleteEndpoint}
                                    />
                                </Tabs.TabPane>

                                <Tabs.TabPane tab={props.intl.formatMessage({ id: 'add' })} key="add-config">
                                    <DisplayAdd
                                        key={`${selectedConfig?.reportName}-add-config`}
                                        currentUser={props.currentUser}
                                        selectedConfig={selectedConfig}
                                        intl={props.intl}
                                        distinctEntitlements={props.distinctEntitlements}
                                    />
                                </Tabs.TabPane>
                                    </Tabs>

                                    <Button
                                        type={'primary'}
                                        onClick={submitData}
                                        style={{ marginTop: '20px', width: '120px' }}
                                        hidden={!selectedConfig}
                                    >
                                        {props.intl.formatMessage({ id: 'survey-main-general-save-button' })}
                                    </Button>
                                </div>
                            </div>
                        </Tabs.TabPane>
                        <Tabs.TabPane
                            tab={props.intl.formatMessage({
                                id: 'reporting-configuration.app-variables.tab',
                                defaultMessage: 'EDP Properties',
                            })}
                            key="edp-properties"
                        >
                            <AppVariableEditor intl={props.intl} />
                        </Tabs.TabPane>
                    </Tabs>
                </Card>
            </Spin>
        </ConfigProvider>
    );
};

export default ReportingConfiguration;

